import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Phone, Mail, MapPin, Clock, Calendar, Facebook, Instagram } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "We'll get back to you as soon as possible.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Have a question or ready to schedule service? Contact us today and our team will be happy to help.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-blue-900" />
                    Phone
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a href="tel:519-709-9242" className="text-lg text-blue-900 hover:underline">
                    519-709-9242
                  </a>
                  <p className="text-sm text-gray-600 mt-2">Available evenings and weekends for emergencies</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-900" />
                    Email
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a href="mailto:info@studeruselectric.com" className="text-lg text-blue-900 hover:underline break-all">
                    info@studeruselectric.com
                  </a>
                  <p className="text-sm text-gray-600 mt-2">We'll respond within 24 hours</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-900" />
                    Service Area
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-2">London, Ontario</p>
                  <p className="text-sm text-gray-600">
                    Serving London, Kingston, Windsor, Chatham, Sarnia, Kitchener, St Thomas, Ingersoll, Woodstock & surrounding areas
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-900" />
                    Business Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-gray-700">
                    <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                    <p>Saturday: Closed</p>
                    <p>Sunday: Closed</p>
                    <p className="text-sm text-gray-600 mt-2">Emergency calls available evenings and weekends</p>
                  </div>
                </CardContent>
              </Card>

              {/* Social Media */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>Follow Us</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">Stay connected on social media</p>
                  <div className="flex gap-4">
                    <a 
                      href="https://www.facebook.com/profile.php?id=61581031466729"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Facebook className="h-5 w-5" />
                      <span className="font-semibold">Facebook</span>
                    </a>
                    <a 
                      href="https://www.instagram.com/studeruselectric/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-colors"
                    >
                      <Instagram className="h-5 w-5" />
                      <span className="font-semibold">Instagram</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="519-709-9242"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service">Service Needed</Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        >
                          <option value="">Select a service</option>
                          <option value="residential">Residential Electrical</option>
                          <option value="commercial">Commercial Electrical</option>
                          <option value="thermal">Thermal Scanning</option>
                          <option value="generator">Generator Installation</option>
                          <option value="highrise">Highrise Turnovers</option>
                          <option value="panel">Panel Upgrades</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your electrical needs..."
                        rows={6}
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button type="submit" size="lg" className="bg-blue-900 hover:bg-blue-800">
                        Send Message
                      </Button>
                      <Button
                        type="button"
                        size="lg"
                        variant="outline"
                        asChild
                      >
                        <a href="tel:519-709-9242">
                          <Phone className="mr-2 h-4 w-4" />
                          Call Instead
                        </a>
                      </Button>
                    </div>
                    
                    {/* Book Online Button */}
                    <div className="pt-6 border-t border-gray-200">
                      <p className="text-gray-700 mb-4 text-center">
                        Prefer to schedule online? Book a time that works for you:
                      </p>
                      <Button
                        type="button"
                        size="lg"
                        variant="default"
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
                        asChild
                      >
                        <a href="https://calendar.app.google/mAWkTnFLgHp2TVZp6" target="_blank" rel="noopener noreferrer">
                          <Calendar className="mr-2 h-5 w-5" />
                          Book Online via Google Calendar
                        </a>
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Service CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Need Emergency Electrical Service?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            We're available evenings and weekends for electrical emergencies
          </p>
          <Button asChild size="lg" className="bg-gray-900 hover:bg-gray-800 text-white">
            <a href="tel:519-709-9242">
              <Phone className="mr-2 h-5 w-5" />
              Call 519-709-9242 Now
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;